import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { Ic } from './icons';
import { STDoc } from './stationery-doc';
import { STStore } from './stationery-store';
import { getV1TemplateHeaderFooterCompanyIdHeaderFooter, patchV1TemplateHeaderFooterId, postV1TemplateHeaderFooter } from '@/api-client/api.system-feature/api';
import { getToken } from '@/utils/common';
import { DndContext, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay } from '@dnd-kit/core';
import { getInitialSectionData, generateContentHTML } from '../utils/common';
import { STATIONERY_CSS } from '../utils/constant';
import EditorSection from '../components/EditorSection';
import EditorSidebar from '../components/EditorSidebar';
import SectionFormatPanel from '../components/SectionFormatPanel';

/* ─────────────────────────────────────────────────────────────
   Stationery designer (global master header & footer)
   A focused editor for the ONE global header + footer
   that every document template inherits.
   ───────────────────────────────────────────────────────────── */

const { useState, useEffect, useRef, useCallback, useMemo } = React;

const DEFAULT_ELEMENT_SIZES: Record<string, { width: number; height: number }> = {
  text: { width: 300, height: 60 },
  'text-block': { width: 300, height: 120 },
  heading: { width: 400, height: 60 },
  'heading-2': { width: 400, height: 50 },
  'heading-4': { width: 400, height: 35 },
  image: { width: 100, height: 100 },
  divider: { width: 600, height: 2 },
  spacer: { width: 600, height: 40 },
  table: { width: 600, height: 60 },
  buttons: { width: 200, height: 60 },
  'qr-code': { width: 150, height: 150 },
  signature: { width: 300, height: 100 },
};

const { PaperSheet, DOC_META } = STDoc;

const BRANDS = ['#DD0D1A', '#1B2C6E', '#C0392B', '#156B66', '#8a540b', '#5535B8', '#14171b'];

const VARIANT_NOTE = {
  standard: 'Logo left, company identity right. The default, balanced masthead.',
  split: 'Logo + website left, bilingual name right — mirrors the client letterhead sample.',
  centered: 'Logo and bilingual name stacked and centred.',
  compact: 'A single slim line — best for dense or continuation-heavy documents.',
};

// ── control primitives ───────────────────────────────────────
function Section({ title, icon, open, onToggle, children, hint }) {
  return (
    <div className={`cx-sec ${open ? 'is-open' : ''}`}>
      <button className="cx-sec-h" onClick={onToggle}>
        <span className="cx-sec-ic"><Ic k={icon} /></span>
        <span className="cx-sec-t">{title}</span>
        <Ic k="chevD" className="cx-sec-chev" />
      </button>
      {open && <div className="cx-sec-body">{hint && <div className="cx-hint">{hint}</div>}{children}</div>}
    </div>
  );
}
const Row = ({ label, sub, children }) => (
  <div className="cx-row">
    <div className="cx-row-l"><span className="cx-row-lbl">{label}</span>{sub && <span className="cx-row-sub">{sub}</span>}</div>
    <div className="cx-row-c">{children}</div>
  </div>
);
const Toggle = ({ on, onChange }) => (
  <button className={`cx-switch ${on ? 'is-on' : ''}`} role="switch" aria-checked={on} onClick={() => onChange(!on)}><span className="cx-switch-dot" /></button>
);
const Seg = ({ value, options, onChange, full }) => (
  <div className={`cx-seg ${full ? 'is-full' : ''}`}>
    {options.map(o => <button key={o.v} className={value === o.v ? 'is-active' : ''} onClick={() => onChange(o.v)}>{o.label}</button>)}
  </div>
);
const Select = ({ value, options, onChange }) => (
  <select className="cx-select" value={value} onChange={e => onChange(e.target.value)}>
    {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
  </select>
);
const Range = ({ value, min, max, step, onChange, suffix }) => (
  <div className="cx-range">
    <input type="range" min={min} max={max} step={step || 1} value={value} onChange={e => onChange(Number(e.target.value))} />
    <span className="cx-range-v">{value}{suffix || ''}</span>
  </div>
);
const Text = ({ value, onChange, placeholder, area, dir }) => area
  ? <textarea className="cx-input cx-area" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />
  : <input className="cx-input" value={value} placeholder={placeholder} dir={dir} onChange={e => onChange(e.target.value)} />;


// ── droppable zone wrapper ────────────────────────────────────
function DropZone({ id, children, isActive }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} style={{
      minHeight: 48,
      border: `2px dashed ${isOver || isActive ? '#2563eb' : '#d1d5db'}`,
      borderRadius: 6,
      background: isOver ? 'rgba(37,99,235,0.04)' : 'transparent',
      transition: 'border-color 0.2s, background 0.2s',
      padding: 4,
      marginTop: 4,
    }}>
      {children}
      {!children && (
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 11, padding: '8px 0' }}>
          Drop elements here
        </div>
      )}
    </div>
  );
}


function Rail({ m, set }) {
  const [open, setOpen] = useState({ brand: true, identity: true, header: false, footer: false, reserve: false, elements: false, page: false });
  const [logoError, setLogoError] = useState('');
  const toggle = (k) => setOpen(o => ({ ...o, [k]: !o[k] }));

  const setH = (p) => set(s => ({ ...s, header: { ...s.header, ...p } }));
  const setF = (p) => set(s => ({ ...s, footer: { ...s.footer, ...p } }));
  const setR = (p: any) => set((s: any) => {
    const newTop = p.top !== undefined ? p.top : (s.reserve?.top || 156);
    const newBottom = p.bottom !== undefined ? p.bottom : (s.reserve?.bottom || 120);

    const updatedHeader = { ...s.header, height: newTop };
    const updatedFooter = { ...s.footer, height: newBottom };

    // Clamp any existing sections so shrinking the space doesn't leave elements out of bounds
    const updatedSections = (s.sections || []).map((sec: any) => {
      const zoneHeight = sec.zone === 'footer' ? newBottom : newTop;
      const secHeight = sec.height || sec.attributes?.height || 40;
      const currentY = sec.y ?? sec.attributes?.y ?? 0;
      if (currentY + secHeight > zoneHeight) {
        const clampedY = Math.max(0, zoneHeight - secHeight);
        return {
          ...sec,
          y: clampedY,
          attributes: {
            ...(sec.attributes || {}),
            y: clampedY
          }
        };
      }
      return sec;
    });

    return {
      ...s,
      reserve: { ...s.reserve, ...p },
      header: updatedHeader,
      footer: updatedFooter,
      sections: updatedSections
    };
  });
  const pre = m.mode === 'preprinted';

  const setReg = (i, p) => setF({ regItems: m.footer.regItems.map((r, j) => j === i ? { ...r, ...p } : r) });
  const addReg = () => setF({ regItems: [...m.footer.regItems, { label: 'New ref', value: '' }] });
  const delReg = (i) => setF({ regItems: m.footer.regItems.filter((_, j) => j !== i) });

  return (
    <div className="cx">
      {/* ELEMENTS */}
      <Section title="Elements" icon="plus" open={open.elements} onToggle={() => toggle('elements')} hint="Drag and drop elements into the header or footer.">
        <div style={{ marginTop: '8px' }}>
          <EditorSidebar />
        </div>
      </Section>
      {/* BRAND COLOUR — independent of doc accent */}
      <Section title="Stationery brand" icon="palette" open={open.brand} onToggle={() => toggle('brand')}
        hint="The header rule and footer band use this colour. It is independent of each document's own accent colour.">
        <Row label="Brand colour">
          <div className="st-brandsw">
            {BRANDS.map(c => <button key={c} className={`cx-swatch ${m.brand === c ? 'is-sel' : ''}`} style={{ background: c }} onClick={() => set(s => ({ ...s, brand: c }))} />)}
            <label className="st-swatch-custom" title="Custom colour" style={{ background: BRANDS.includes(m.brand) ? '' : m.brand }}>
              {BRANDS.includes(m.brand) && <Ic k="plus" />}
              <input type="color" value={m.brand} onChange={e => set(s => ({ ...s, brand: e.target.value }))} />
            </label>
          </div>
        </Row>
      </Section>

      {/* COMPANY IDENTITY */}
      <Section title="Company identity" icon="bank" open={open.identity} onToggle={() => toggle('identity')}
        hint="Brand-level fields shared by every document. The client supplies these once; all templates inherit them.">
        <Row label="Logo"><Toggle on={m.header.showLogo} onChange={v => setH({ showLogo: v })} /></Row>
        {m.header.showLogo && <>
          <div className="st-logo-up">
            <div className="st-logo-up-preview" style={{ width: 60, height: 60, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={m.header.logoSrc || 'assets/altrad-spar-logo.png'} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
            <div className="st-logo-up-actions">
              <label className="cx-btn cx-btn--sm">
                <Ic k="image" />{m.header.logoSrc ? 'Replace' : 'Upload logo'}
                <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) { if (f.size > 300 * 1024) { setLogoError('Please select an image under 300KB.'); e.target.value = ''; return; } setLogoError(''); const r = new FileReader(); r.onload = () => setH({ logoSrc: r.result, showLogo: true }); r.readAsDataURL(f); } e.target.value = ''; }} />
              </label>
              {m.header.logoSrc && <button className="cx-btn cx-btn--sm cx-btn--ghost" onClick={() => setH({ logoSrc: null })}><Ic k="undo" />Default</button>}
            </div>
            {logoError && <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>{logoError}</div>}
          </div>
        </>}
        <div className="cx-divline" />
        <div className="cx-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <span className="cx-row-lbl">Company name (English)</span>
          <Text value={m.header.nameEN} onChange={v => setH({ nameEN: v })} placeholder="Company legal name" />
        </div>
        <Row label="Bilingual (Arabic)" sub="Show an Arabic name alongside"><Toggle on={m.header.bilingual} onChange={v => setH({ bilingual: v })} /></Row>
        {m.header.bilingual && (
          <div className="cx-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
            <span className="cx-row-lbl">Company name (Arabic · RTL)</span>
            <Text value={m.header.nameAR} onChange={v => setH({ nameAR: v })} placeholder="الاسم بالعربية" dir="rtl" />
          </div>
        )}
        <div className="cx-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
          <span className="cx-row-lbl">Tagline / website</span>
          <Text value={m.header.tagline} onChange={v => setH({ tagline: v })} placeholder="www.example.com" />
        </div>
      </Section>



      {/* HEADER MASTER */}
      <Section title="Header master" icon="panelL" open={open.header} onToggle={() => toggle('header')}
        hint="The letterhead band at the top of the page. One header, inherited by every template.">
        <Row label="Show header"><Toggle on={m.header.show} onChange={v => setH({ show: v })} /></Row>
        {m.header.show && <>
          <Row label="Layout"><Select value={m.header.variant} options={[{ v: 'standard', label: 'Standard' }, { v: 'split', label: 'Split (client style)' }, { v: 'centered', label: 'Centered' }, { v: 'compact', label: 'Compact' }]} onChange={v => {
            const stripPos = (attr: any) => {
              if (!attr) return attr;
              const { x, y, width, height, rotation, ...rest } = attr;
              return rest;
            };
            setH({
              variant: v,
              logoAttributes: stripPos(m.header.logoAttributes),
              nameENAttributes: stripPos(m.header.nameENAttributes),
              nameARAttributes: stripPos(m.header.nameARAttributes),
              taglineAttributes: stripPos(m.header.taglineAttributes),
              ruleAttributes: stripPos(m.header.ruleAttributes)
            });
          }} /></Row>
          <div className="st-variantnote">{VARIANT_NOTE[m.header.variant]}</div>
          <Row label="Brand rule"><Toggle on={m.header.rule} onChange={v => setH({ rule: v })} /></Row>
          <div className="cx-divline" />
          <Row label="Repeat on"><Select value={m.header.repeat} options={[{ v: 'all', label: 'All pages' }, { v: 'first', label: 'First page only' }]} onChange={v => setH({ repeat: v })} /></Row>
          {m.header.repeat === 'all' && <Row label="Slim header on page 2+" sub="Save space on continuation"><Toggle on={m.header.compactCont} onChange={v => setH({ compactCont: v })} /></Row>}
        </>}
      </Section>

      {/* FOOTER MASTER */}
      <Section title="Footer master" icon="panelR" open={open.footer} onToggle={() => toggle('footer')}
        hint="Industry-standard footer — registration numbers, contact line and page numbers. It never repeats anything already shown in the header.">
        <Row label="Show footer"><Toggle on={m.footer.show} onChange={v => setF({ show: v })} /></Row>
        {m.footer.show && <>
          <div className="cx-divline" />
          <Row label="Registration band" sub="Coloured CR / VAT / TRN strip"><Toggle on={m.footer.regBand} onChange={v => {
            const stripPos = (attr: any) => {
              if (!attr) return attr;
              const { x, y, width, height, rotation, ...rest } = attr;
              return rest;
            };
            setF({ regBand: v, regBandAttributes: v ? stripPos(m.footer.regBandAttributes) : m.footer.regBandAttributes });
          }} /></Row>
          {m.footer.regBand && (
            <div className="st-reg">
              {m.footer.regItems.map((r, i) => (
                <div className="st-reg-row" key={i}>
                  <input className="cx-input st-reg-lbl" value={r.label} onChange={e => setReg(i, { label: e.target.value })} placeholder="Label" />
                  <input className="cx-input" value={r.value} onChange={e => setReg(i, { value: e.target.value })} placeholder="Number" />
                  <button className="st-reg-del" onClick={() => delReg(i)} title="Remove"><Ic k="trash" /></button>
                </div>
              ))}
              <button className="cx-add" onClick={addReg}><Ic k="plus" />Add registration number</button>
            </div>
          )}
          <div className="cx-divline" />
          <Row label="Contact line" sub="Address · phone · email"><Toggle on={m.footer.contactRow} onChange={v => setF({ contactRow: v })} /></Row>
          {m.footer.contactRow && <>
            <div className="cx-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}><span className="cx-row-lbl">Address</span><Text value={m.footer.address} onChange={v => setF({ address: v })} /></div>
            <Row label="Phone"><Text value={m.footer.phone} onChange={v => setF({ phone: v })} /></Row>
            <Row label="Email"><Text value={m.footer.email} onChange={v => setF({ email: v })} /></Row>
          </>}
          <div className="cx-divline" />
          <Row label="Page numbers"><Toggle on={m.footer.pageNumbers} onChange={v => setF({ pageNumbers: v })} /></Row>
          {m.footer.pageNumbers && <Row label="Format"><Select value={m.footer.format} options={[{ v: 'Page {n} of {total}', label: 'Page 1 of N' }, { v: '{n} / {total}', label: '1 / N' }, { v: 'Page {n}', label: 'Page 1' }]} onChange={v => setF({ format: v })} /></Row>}
          <div className="cx-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}><span className="cx-row-lbl">System note</span><Text value={m.footer.note} onChange={v => setF({ note: v })} placeholder="Optional fine-print" /></div>
          <Row label="Repeat on"><Select value={m.footer.repeat} options={[{ v: 'all', label: 'All pages' }, { v: 'first', label: 'First page only' }]} onChange={v => setF({ repeat: v })} /></Row>
        </>}
      </Section>

      {/* HEADER & FOOTER MARGINS */}
      <Section title="Header & footer margins" icon="file" open={open.reserve} onToggle={() => toggle('reserve')}
        hint="Set the allowed header and footer boundary heights. Canvas elements are strictly constrained within these allowed spaces in both Letterhead and Pre-printed modes.">
        <Row label="Header space"><Range value={m.reserve?.top || m.header?.height || 156} min={50} max={400} onChange={v => setR({ top: v })} suffix="px" /></Row>
        <Row label="Footer space"><Range value={m.reserve?.bottom || m.footer?.height || 120} min={40} max={300} onChange={v => setR({ bottom: v })} suffix="px" /></Row>
      </Section>

      {/* PAGE SETUP */}
      <Section title="Page setup" icon="file" open={open.page} onToggle={() => toggle('page')}
        hint="Choose standard paper size and orientation. The header and footer layout will adapt to the chosen page dimensions.">
        <Row label="Paper size">
          <Seg full value={m.paper || 'A4'} options={[{ v: 'A4', label: 'A4' }, { v: 'Letter', label: 'Letter' }]} onChange={v => {
            const newW = (v === 'Letter' ? (m.orient === 'landscape' ? 1056 : 816) : (m.orient === 'landscape' ? 1123 : 794));
            set(s => ({
              ...s,
              paper: v,
              header: {
                ...(s.header || {}),
                ruleAttributes: {
                  ...(s.header?.ruleAttributes || {}),
                  x: 0,
                  width: newW,
                  w: newW
                }
              },
              footer: {
                ...(s.footer || {}),
                regBandAttributes: {
                  ...(s.footer?.regBandAttributes || {}),
                  x: 0,
                  width: newW,
                  w: newW
                }
              },
              sections: (s.sections || []).map((sec: any) => ({
                ...sec,
                x: Math.min(sec.x ?? 0, Math.max(0, newW - (sec.width || 200))),
                attributes: {
                  ...(sec.attributes || {}),
                  x: Math.min(sec.attributes?.x ?? sec.x ?? 0, Math.max(0, newW - (sec.width || 200)))
                }
              }))
            }));
          }} />
        </Row>
        <Row label="Orientation">
          <Seg full value={m.orient || 'portrait'} options={[{ v: 'portrait', label: 'Portrait' }, { v: 'landscape', label: 'Landscape' }]} onChange={v => {
            const newW = (m.paper === 'Letter' ? (v === 'landscape' ? 1056 : 816) : (v === 'landscape' ? 1123 : 794));
            set(s => ({
              ...s,
              orient: v,
              header: {
                ...(s.header || {}),
                ruleAttributes: {
                  ...(s.header?.ruleAttributes || {}),
                  x: 0,
                  width: newW,
                  w: newW
                }
              },
              footer: {
                ...(s.footer || {}),
                regBandAttributes: {
                  ...(s.footer?.regBandAttributes || {}),
                  x: 0,
                  width: newW,
                  w: newW
                }
              },
              sections: (s.sections || []).map((sec: any) => ({
                ...sec,
                x: Math.min(sec.x ?? 0, Math.max(0, newW - (sec.width || 200))),
                attributes: {
                  ...(sec.attributes || {}),
                  x: Math.min(sec.attributes?.x ?? sec.x ?? 0, Math.max(0, newW - (sec.width || 200)))
                }
              }))
            }));
          }} />
        </Row>
      </Section>

    </div>
  );
}

// ── canvas ───────────────────────────────────────────────────
const DOC_ORDER = ['quotation', 'invoice', 'delivery'];
const DOC_IC = { quotation: 'quote', invoice: 'doc', delivery: 'truck' };

function Stage({ m, type, setType, showCont, setShowCont, sample, setSample, ed, lens, setLens, count }) {
  const scrollRef = useRef(null);
  const [fit, setFit] = useState(0.74);
  const [zoom, setZoom] = useState('fit');

  const paper = m.paper || 'A4';
  const orient = m.orient || 'portrait';
  let [pageW, pageH] = paper === 'Letter' ? [816, 1056] : [794, 1123];
  if (orient === 'landscape') { const t = pageW; pageW = pageH; pageH = t; }

  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const calc = () => setFit(Math.max(0.3, Math.min(1, (el.clientWidth - 56) / pageW)));
    calc(); const ro = new ResizeObserver(calc); ro.observe(el); return () => ro.disconnect();
  }, [pageW]);
  const z = zoom === 'fit' ? fit : zoom;
  const pages = (sample && showCont) ? [0, 1] : [0];


  return (
    <div className="pb-stage st-stage" style={{ position: 'relative' }}>
      <div className="st-toolbar">
        <div className="pb-lens">
          <button className={lens === 'edit' ? 'is-active' : ''} onClick={() => setLens('edit')}><Ic k="type" />Edit</button>
          <button className={lens === 'preview' ? 'is-active' : ''} onClick={() => setLens('preview')}><Ic k="eye" />Preview</button>
        </div>
        {lens === 'edit' && m.mode === 'letterhead'
          ? <span className="st-edithint"><Ic k="type" />Double-click text to edit · Click and drag elements to move · Click logo to replace</span>
          : <span className="st-toolbar-lbl"><Ic k="layers" />Editing "{m.name}" · {count} in your library</span>}
        <div className="st-toolbar-sp" />
        <button className={`st-sample-tg ${sample ? 'is-on' : ''}`} onClick={() => setSample(v => !v)}
          title="Preview the master wrapped around a real document">
          <span className="cx-switch" aria-hidden><span className="cx-switch-dot" /></span>
          {sample ? 'Sample content' : 'Show sample content'}
        </button>
        {sample && (
          <div className="st-docchips">
            {DOC_ORDER.map(d => (
              <button key={d} className={`st-docchip ${type === d ? 'is-active' : ''}`} onClick={() => setType(d)}>
                <span className="st-docchip-dot" style={{ background: DOC_META[d].accent }} />
                <Ic k={DOC_IC[d]} />{DOC_META[d].name}
              </button>
            ))}
            <button className={`st-docchip ${showCont ? 'is-active' : ''}`} onClick={() => setShowCont(v => !v)} title="Add a continuation page to test header/footer repeat">
              <Ic k="copy" />{showCont ? '2 pages' : '1 page'}
            </button>
          </div>
        )}
      </div>

      <div className="pb-stage-scroll" ref={scrollRef}>
        <div className="pb-zoomwrap" style={{ transform: `scale(${z})`, transformOrigin: 'top center' }}>
          <div className="pb-pages st" style={{ ['--st-brand']: m.brand }}>
            {pages.map(pi => {
              const headerRepeat = m.header.repeat || 'all';
              const headerShown = m.mode !== 'preprinted' && m.header.show && (headerRepeat === 'all' || pi === 0);
              const compactHead = headerRepeat === 'all' && m.header.compactCont && pi > 0;

              const footerRepeat = m.footer.repeat || 'all';
              const footerShown = m.mode !== 'preprinted' && m.footer.show && (footerRepeat === 'all' || pi === 0);

              return (
                <div
                  key={pi}
                  className="pbd-page"
                  data-screen-label={`Page ${pi + 1}`}
                  style={{
                    width: `${pageW}px`,
                    minWidth: `${pageW}px`,
                    maxWidth: `${pageW}px`,
                    minHeight: `${pageH}px`,
                    ['--pbd-page-w']: `${pageW}px`,
                    ['--pbd-page-h']: `${pageH}px`,
                  }}
                >
                  {/* Custom header drop zone or standard header */}
                  {headerShown && (
                    <div>
                      <STDoc.Header m={m} compact={compactHead} ed={(ed?.on && pi === 0 && !compactHead) ? ed : null} />
                    </div>
                  )}

                  {/* Pre-printed top reserve */}
                  {m.mode === 'preprinted' && (
                    <div className="st-reserve st-reserve--top" style={{ height: m.reserve.top }}>
                      <span><Ic k="panelL" />Reserved · pre-printed letterhead ({m.reserve.top}px)</span>
                    </div>
                  )}

                  {/* Body */}
                  <div className="pbd pbd-page-inner" data-density="compact" style={{ ['--pbd-accent']: DOC_META[type]?.accent }}>
                    {sample
                      ? <STDoc.Body type={type} pageIndex={pi} total={pages.length} />
                      : <div className="st-blank"><span>{ed?.on ? 'Document content appears here · edit the header & footer around it' : 'Blank stationery · content area'}</span></div>}
                  </div>

                  {/* Custom footer drop zone or standard footer */}
                  {footerShown && (
                    <div>
                      <STDoc.Footer m={m} pageIndex={pi} total={pages.length} compact={false} ed={(ed?.on && pi === 0) ? ed : null} />
                    </div>
                  )}

                  {/* Pre-printed bottom reserve */}
                  {m.mode === 'preprinted' && (
                    <div className="st-reserve st-reserve--bot" style={{ height: m.reserve.bottom }}>
                      <span><Ic k="panelR" />Reserved · pre-printed footer ({m.reserve.bottom}px)</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="pb-zoomfloat">
        <button onClick={() => setZoom(Math.max(0.3, z - 0.1))}><Ic k="minus" /></button>
        <span className="pb-zoom-val">{Math.round(z * 100)}%</span>
        <button onClick={() => setZoom(Math.min(1.6, z + 0.1))}><Ic k="plus" /></button>
        <button onClick={() => setZoom('fit')} title="Fit width"><Ic k="fit" /></button>
        <span className="pb-zoom-pages">{m.mode === 'preprinted' ? 'Pre-printed' : 'Letterhead'} · {paper}{orient === 'landscape' ? ' (Landscape)' : ''}</span>
      </div>
    </div>
  );
}

// ── app ──────────────────────────────────────────────────────
export const StationeryMasterApp = ({ onBack, company_id, initialPresetId, isNewTemplate, placeholders, relation_table, initialPaper, initialOrient }: { onBack: (lib?: any, activeId?: string) => void, company_id?: number, initialPresetId?: string, isNewTemplate?: boolean, placeholders?: any[], relation_table?: any[], initialPaper?: string, initialOrient?: string }) => {
  const [lib, setLib] = useState(() => {
    const loaded = STStore.load();
    if (initialPaper || initialOrient) {
      const act = STStore.getActive(loaded);
      if (act) {
        if (initialPaper) act.paper = initialPaper;
        if (initialOrient) act.orient = initialOrient;
      }
    }
    return loaded;
  });
  const [hfId, setHfId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState('quotation');
  const [showCont, setShowCont] = useState(false);
  const [sample, setSample] = useState(false);
  const [lens, setLens] = useState('edit');
  const [savedJSON, setSavedJSON] = useState(() => JSON.stringify(lib));
  const [toast, setToast] = useState(null);
  const tT = useRef(null);
  const flash = (msg) => { setToast(msg); clearTimeout(tT.current); tT.current = setTimeout(() => setToast(null), 2000); };

  const active = STStore.getActive(lib);
  const dirty = JSON.stringify(lib) !== savedJSON;

  // Reset the dirty baseline (persistence to localStorage has been removed)
  const persist = useCallback((L) => { setSavedJSON(JSON.stringify(L)); }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!company_id) return;
      try {
        setIsLoading(true);
        const token = getToken();
        const res = await getV1TemplateHeaderFooterCompanyIdHeaderFooter({
          ...token,
          'x-timezone': token['x-timezone']?.toString() || '',
          company_id: Number(company_id)
        } as any);
        if ((res.status_code === 200 || res.success) && res.data) {
          const hfData = Array.isArray(res.data) ? res.data[0] : res.data;
          if (!hfData) return;
          setHfId(hfData._id || hfData.id);
          const v2Section = (hfData.header_sections || []).find((s: any) => s.attributes?.stationeryV2Config || s.type === 'stationery-v2');
          if (v2Section) {
            try {
              let loadedLib;
              if (v2Section.attributes?.stationeryV2Config) {
                loadedLib = v2Section.attributes.stationeryV2Config;
                if (typeof loadedLib === 'string') loadedLib = JSON.parse(loadedLib);
              } else if (v2Section.content) {
                loadedLib = JSON.parse(v2Section.content);
              }

              if (loadedLib) {
                if (isNewTemplate && initialPresetId === '__blank__') {
                  // Auto-create a new preset for new templates starting with a blank stationery
                  const p = STStore.defPreset({ name: 'New stationery', paper: initialPaper || 'A4', orient: initialOrient || 'portrait' });
                  loadedLib.presets.push(p);
                  loadedLib.activeId = p.id;
                } else if (initialPresetId && initialPresetId !== '__blank__') {
                  const exists = loadedLib.presets.find((p: any) => p.id === initialPresetId);
                  if (exists) {
                    loadedLib.activeId = initialPresetId;
                  }
                }
                if (initialPaper || initialOrient) {
                  const act = loadedLib.presets.find((p: any) => p.id === loadedLib.activeId);
                  if (act) {
                    if (initialPaper) act.paper = initialPaper;
                    if (initialOrient) act.orient = initialOrient;
                  }
                }
                setLib(loadedLib);
                persist(loadedLib);
              }
            } catch (e) {
              console.error('Failed to parse stationery-v2 content', e);
            }
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [company_id, persist]);

  const save = useCallback(async () => {
    try {
      if (!company_id) {
        flash('Cannot save: Company ID is missing');
        return;
      }
      flash('Saving to backend...');

      const activePreset = STStore.getActive(lib);
      const rawHeaderHtml = renderToStaticMarkup(<DndContext><STDoc.Header m={activePreset} compact={false} ed={null} /></DndContext>);
      const rawFooterHtml = renderToStaticMarkup(<DndContext><STDoc.Footer m={activePreset} pageIndex={0} total={1} compact={false} ed={null} /></DndContext>);

      const token = getToken();
      const payload = {
        ...token,
        company_id: Number(company_id),
        header_sections: [
          {
            id: 'stationery-compiled-header',
            type: 'text',
            content: STATIONERY_CSS + rawHeaderHtml,
            isSplitPair: false,
            splitGroupId: null,
            splitWidth: null,
            attributes: { stationeryV2Config: lib }
          },
          ...(activePreset.sections || []).filter((s: any) => s.zone === 'header')
        ],
        footer_sections: [
          {
            id: 'stationery-compiled-footer',
            type: 'text',
            content: STATIONERY_CSS + rawFooterHtml,
            isSplitPair: false,
            splitGroupId: null,
            splitWidth: null,
            attributes: {}
          },
          ...(activePreset.sections || []).filter((s: any) => s.zone === 'footer')
        ],
        contents: '<!-- V2 Stationery Config -->'
      };

      if (hfId) {
        await patchV1TemplateHeaderFooterId({ ...payload, id: hfId } as any);
      } else {
        const res: any = await postV1TemplateHeaderFooter(payload as any);
        if (res.status_code === 200 && res.data) {
          setHfId(res.data.id);
        }
      }
      persist(lib);
      flash('Stationery library saved successfully');
    } catch (e) {
      console.error(e);
      flash('Failed to save to backend');
    }
  }, [lib, persist, company_id, hfId]);

  // edit the ACTIVE preset (design content → dirty until Save)
  const setM = useCallback((updater) => {
    setLib(L => {
      const cur = STStore.getActive(L);
      const next = typeof updater === 'function' ? updater(cur) : updater;
      return { ...L, presets: L.presets.map(p => p.id === cur.id ? next : p) };
    });
  }, []);

  // ── structural ops (auto-saved so switching never looks "unsaved") ──
  const switchPreset = (id) => { const N = { ...lib, activeId: id }; setLib(N); persist(N); };
  const newPreset = () => {
    const p = STStore.defPreset({ name: 'New stationery' });
    const N = { ...lib, presets: [...lib.presets, p], activeId: p.id }; setLib(N); persist(N); flash('New stationery added');
  };
  const dupPreset = () => {
    const copy = STStore.mergePreset({ ...active, id: STStore.uid(), name: active.name + ' copy' });
    const N = { ...lib, presets: [...lib.presets, copy], activeId: copy.id }; setLib(N); persist(N); flash('Duplicated');
  };
  const delPreset = () => {
    if (lib.presets.length <= 1) return;
    if (!window.confirm(`Delete the stationery "${active.name}"? Documents using it will fall back to the first preset.`)) return;
    const presets = lib.presets.filter(p => p.id !== active.id);
    const N = { presets, activeId: presets[0].id }; setLib(N); persist(N); flash('Stationery deleted');
  };

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && (e.key === 's' || e.key === 'S')) { e.preventDefault(); if (dirty) save(); } };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [dirty, save]);



  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // ── drag-drop: append element to customHeader or customFooter ─
  const handleDragStart = ({ active }) => setActiveDragId(active.id);
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over) return;

    const isHeaderZone = over.id === 'st-drop-header';
    const isFooterZone = over.id === 'st-drop-footer';

    const targetHeaderSection = (m.sections || []).find((s: any) => s.zone === 'header' && s.id === over.id);
    const targetFooterSection = (m.sections || []).find((s: any) => s.zone === 'footer' && s.id === over.id);

    const zone = isHeaderZone || targetHeaderSection ? 'header' : isFooterZone || targetFooterSection ? 'footer' : null;
    if (!zone) return;

    const activeIdStr = String(active.id);
    const isNewElement = activeIdStr.startsWith('sidebar-') || activeIdStr.startsWith('st-elem-') || activeIdStr.startsWith('draggable-');

    if (!isNewElement) {
      // Moving an existing element
      const sourceSection = (m.sections || []).find((s: any) => s.id === active.id);
      if (!sourceSection) return;

      if (targetHeaderSection || targetFooterSection) {
        // Swap with target
        const targetId = over.id;
        const targetSection = (m.sections || []).find((s: any) => s.id === targetId);
        if (!targetSection || targetSection.id === sourceSection.id) return;

        setM((s: any) => {
          const newS = { ...s };
          newS.sections = (newS.sections || []).map((sec: any) => {
            if (sec.id === targetId) return { ...sec, type: sourceSection.type, content: sourceSection.content, attributes: sourceSection.attributes };
            if (sec.id === active.id) return { ...sec, type: targetSection.type, content: targetSection.content, attributes: targetSection.attributes, zone: targetSection.zone || zone };
            return sec;
          });
          return newS;
        });
        flash('Moved element');
      } else {
        // Moved to empty generic zone (append)
        setM((s: any) => {
          const newS = { ...s };
          newS.sections = (newS.sections || []).map((sec: any) => sec.id === active.id ? { ...sec, zone } : sec);
          return newS;
        });
        flash('Moved element');
      }
      return;
    }

    // Adding a new element from the sidebar
    const elemType = (active.data?.current?.type || activeIdStr.replace('st-elem-', '').replace('sidebar-', '').replace('draggable-', '').split('-')[0]) as any;
    const imageUrl = active.data?.current?.imageUrl;
    let newSection: any = { ...getInitialSectionData(elemType), id: 'st-' + Math.random().toString(36).slice(2, 9), zone };
    if (elemType === 'image' && imageUrl) {
      newSection = { ...newSection, content: `<img src="${imageUrl}" alt="Image" style="max-width:100%;max-height:100%;object-fit:contain;width:100%;height:100%;box-sizing:border-box;" />` };
    }

    const sizes = DEFAULT_ELEMENT_SIZES[elemType] || { width: 200, height: 80 };
    newSection.width = sizes.width;
    newSection.height = sizes.height;
    newSection.rotation = 0;

    // Calculate actual drop position relative to the zone
    if (active.rect?.current?.translated && over.rect) {
      const activePageW = (m.paper === 'Letter' ? (m.orient === 'landscape' ? 1056 : 816) : (m.orient === 'landscape' ? 1123 : 794));
      const scale = over.rect.width / activePageW;
      
      const pointerX = active.rect.current.translated.left + active.rect.current.translated.width / 2;
      const pointerY = active.rect.current.translated.top + active.rect.current.translated.height / 2;
      
      let dropX = (pointerX - over.rect.left - (sizes.width * scale) / 2) / scale;
      let dropY = (pointerY - over.rect.top - (sizes.height * scale) / 2) / scale;

      const zoneHeight = zone === 'header' ? (m.header?.height || m.reserve?.top || 156) : (m.footer?.height || m.reserve?.bottom || 120);
      dropX = Math.max(0, Math.min(dropX, activePageW - sizes.width));
      dropY = Math.max(0, Math.min(dropY, zoneHeight - sizes.height));

      newSection.x = dropX;
      newSection.y = dropY;
    } else {
      newSection.x = 50;
      const zoneHeight = zone === 'header' ? (m.header?.height || m.reserve?.top || 156) : (m.footer?.height || m.reserve?.bottom || 120);
      newSection.y = Math.min(20, Math.max(0, zoneHeight - sizes.height));
    }

    // Merge root geometry into attributes
    newSection.attributes = {
      ...newSection.attributes,
      width: newSection.width,
      height: newSection.height,
      rotation: newSection.rotation,
      x: newSection.x,
      y: newSection.y
    };

    if (targetHeaderSection || targetFooterSection) {
      const targetId = over.id as string;
      setM((s: any) => ({
        ...s,
        sections: (s.sections || []).map((sec: any) => sec.id === targetId ? { ...sec, type: newSection.type, content: newSection.content, attributes: newSection.attributes } : sec)
      }));
      setSelectedElement(targetId);
      flash(`Added ${elemType} to section`);
    } else {
      setM((s: any) => ({ ...s, sections: [...(s.sections || []), newSection] }));
      setSelectedElement(newSection.id);
      flash(`Added ${elemType} to ${zone}`);
    }
  };


  // inline-edit api passed to the canvas (header/footer/logo) — all target the active preset
  const onLogo = useCallback((file) => {
    if (file.size > 300 * 1024) {
      flash('Please select an image under 300KB.');
      return;
    }
    const r = new FileReader();
    r.onload = () => { setM(s => ({ ...s, header: { ...s.header, logoSrc: r.result, showLogo: true } })); flash('Logo updated'); };
    r.readAsDataURL(file);
  }, [setM]);

  const onChangeElement = useCallback((id, x, y, width, height, rotation) => {
    setM(s => {
      const sPageW = (s.paper === 'Letter' ? (s.orient === 'landscape' ? 1056 : 816) : (s.orient === 'landscape' ? 1123 : 794));
      const isFooter = ['regBand', 'contact', 'note', 'pageNumbers'].includes(id) || 
                       (s.sections || []).some((sec: any) => sec.id === id && sec.zone === 'footer');
      const zoneHeight = isFooter ? (s.footer?.height || s.reserve?.bottom || 120) : (s.header?.height || s.reserve?.top || 156);

      const clampedX = Math.max(0, Math.min(x, sPageW - (width || 20)));
      const clampedY = Math.max(0, Math.min(y, zoneHeight - (height || 20)));

      const attrs = { x: clampedX, y: clampedY, width, height, rotation };
      if (id === 'logo') return { ...s, header: { ...s.header, logoAttributes: { ...(s.header.logoAttributes || {}), ...attrs } } };
      if (id === 'nameEN') return { ...s, header: { ...s.header, nameENAttributes: { ...(s.header.nameENAttributes || {}), ...attrs } } };
      if (id === 'nameAR') return { ...s, header: { ...s.header, nameARAttributes: { ...(s.header.nameARAttributes || {}), ...attrs } } };
      if (id === 'tagline') return { ...s, header: { ...s.header, taglineAttributes: { ...(s.header.taglineAttributes || {}), ...attrs } } };
      if (id === 'rule') return { ...s, header: { ...s.header, ruleAttributes: { ...(s.header.ruleAttributes || {}), ...attrs } } };
      if (id === 'regBand') return { ...s, footer: { ...s.footer, regBandAttributes: { ...(s.footer.regBandAttributes || {}), ...attrs } } };
      if (id === 'contact') return { ...s, footer: { ...s.footer, contactAttributes: { ...(s.footer.contactAttributes || {}), ...attrs } } };
      if (id === 'note') return { ...s, footer: { ...s.footer, noteAttributes: { ...(s.footer.noteAttributes || {}), ...attrs } } };
      if (id === 'pageNumbers') return { ...s, footer: { ...s.footer, pageNumbersAttributes: { ...(s.footer.pageNumbersAttributes || {}), ...attrs } } };

      if (s.sections?.some((sec: any) => sec.id === id)) {
        return {
          ...s,
          sections: s.sections.map((sec: any) => sec.id === id ? { ...sec, x: clampedX, y: clampedY, width, height, rotation, attributes: { ...(sec.attributes || {}), ...attrs } } : sec)
        };
      }
      return s;
    });
  }, [setM]);

  const onDeleteElement = useCallback((id) => {
    setM(s => {
      const newS = { ...s };
      if (id === 'logo') newS.header = { ...newS.header, showLogo: false };
      else if (id === 'nameEN') newS.header = { ...newS.header, nameEN: '' };
      else if (id === 'nameAR') newS.header = { ...newS.header, nameAR: '' };
      else if (id === 'tagline') newS.header = { ...newS.header, tagline: '' };
      else if (id === 'rule') newS.header = { ...newS.header, rule: false };
      else if (id === 'regBand') newS.footer = { ...newS.footer, regBand: false };
      else if (id === 'contact') newS.footer = { ...newS.footer, contactRow: false };
      else if (id === 'meta') newS.footer = { ...newS.footer, note: '', pageNumbers: false };

      if (s.sections?.some((sec: any) => sec.id === id)) {
        newS.sections = s.sections.filter((sec: any) => sec.id !== id);
      }
      return newS;
    });
    setSelectedElement(null);
  }, [setM]);

  const selectedElementData = useMemo(() => {
    if (!selectedElement) return null;
    let type: any = 'text';
    let content = '';
    let attrs: any = {};
    if (selectedElement === 'logo') { type = 'image'; content = ''; attrs = { ...(active.header.logoAttributes || {}), src: active.header.logoSrc || '' }; }
    else if (selectedElement === 'nameEN') { type = 'text'; content = active.header.nameEN || ''; attrs = active.header.nameENAttributes || {}; }
    else if (selectedElement === 'nameAR') { type = 'text'; content = active.header.nameAR || ''; attrs = active.header.nameARAttributes || {}; }
    else if (selectedElement === 'tagline') { type = 'text'; content = active.header.tagline || ''; attrs = active.header.taglineAttributes || {}; }
    else if (selectedElement === 'rule' || selectedElement === 'regBand') { return null; }
    else if (selectedElement === 'contact') { type = 'text'; content = ''; attrs = active.footer.contactAttributes || {}; }
    else if (selectedElement === 'note') { type = 'text'; content = active.footer.note || ''; attrs = active.footer.noteAttributes || {}; }
    else if (selectedElement === 'pageNumbers') { type = 'text'; content = ''; attrs = active.footer.pageNumbersAttributes || {}; }
    else {
      const custom = (active.sections || []).find((s: any) => s.id === selectedElement);
      if (custom) {
        type = custom.type;
        content = custom.content;
        attrs = custom.attributes || {};
      }
    }

    // Sync root width/height to styles so SectionFormatPanel can read them
    return {
      id: selectedElement,
      type,
      content,
      attributes: {
        ...attrs,
        styles: { ...attrs.styles, width: attrs.width, height: attrs.height }
      }
    };
  }, [selectedElement, active]);

  const handleElementFormatUpdate = useCallback((updatedData: any, targetId?: string) => {
    const id = targetId || selectedElement;
    if (!id) return;
    setM((s: any) => {
      const newS = { ...s };
      const custom = (newS.sections || []).find((sec: any) => sec.id === id);

      if (custom) {
        const newAttributes = { ...updatedData.attributes };

        if (newAttributes.styles) {
          if (newAttributes.styles.width !== undefined) {
            const parsed = parseFloat(newAttributes.styles.width);
            if (!isNaN(parsed)) newAttributes.width = parsed;
          }
          if (newAttributes.styles.height !== undefined) {
            const parsed = parseFloat(newAttributes.styles.height);
            if (!isNaN(parsed)) newAttributes.height = parsed;
          }
        }

        const mergedAttributes = { ...custom.attributes, ...newAttributes };

        // For image elements: if src is not in attributes, extract it from existing content
        // so that style-only updates (like alignment) don't lose the image source
        if (custom.type === 'image' && !mergedAttributes.src) {
          const srcMatch = custom.content?.match(/src="([^"]+)"/);
          if (srcMatch?.[1]) mergedAttributes.src = srcMatch[1];
        }

        const updatedContent = generateContentHTML(custom.type as any, updatedData.content !== undefined ? updatedData.content : custom.content, mergedAttributes, []);

        newS.sections = newS.sections.map((sec: any) => {
          if (sec.id === id) {
            const updatedSec = { ...sec, attributes: mergedAttributes, content: updatedContent };
            if (updatedData.width !== undefined) {
              updatedSec.width = updatedData.width;
              updatedSec.attributes.width = updatedData.width;
            }
            if (updatedData.height !== undefined) {
              updatedSec.height = updatedData.height;
              updatedSec.attributes.height = updatedData.height;
            }
            if (updatedData.x !== undefined) {
              updatedSec.x = updatedData.x;
              updatedSec.attributes.x = updatedData.x;
            }
            if (updatedData.y !== undefined) {
              updatedSec.y = updatedData.y;
              updatedSec.attributes.y = updatedData.y;
            }
            return updatedSec;
          }
          return sec;
        });
        return newS;
      }

      let targetZone = 'header';
      let attrKey = `${id}Attributes`;
      if (['regBand', 'contact', 'note', 'pageNumbers'].includes(id)) targetZone = 'footer';

      const existingAttrs = newS[targetZone][attrKey] || {};

      // Sync styles width/height back to root
      const newAttributes = { ...updatedData.attributes };
      if (newAttributes.styles) {
        if (newAttributes.styles.width !== undefined) {
          const parsed = parseFloat(newAttributes.styles.width);
          if (!isNaN(parsed)) newAttributes.width = parsed;
        }
        if (newAttributes.styles.height !== undefined) {
          const parsed = parseFloat(newAttributes.styles.height);
          if (!isNaN(parsed)) newAttributes.height = parsed;
        }
      }

      if (id === 'logo' && newAttributes.src !== undefined) {
        newS.header.logoSrc = newAttributes.src;
        // Clean up src from attributes so it doesn't duplicate state
        delete newAttributes.src;
      }

      newS[targetZone] = { ...newS[targetZone], [attrKey]: { ...existingAttrs, ...newAttributes } };

      const stripHtml = (html: string) => html.replace(/<[^>]*>?/gm, '');

      if (id === 'nameEN' && updatedData.content !== undefined) newS.header.nameEN = stripHtml(updatedData.content);
      if (id === 'nameAR' && updatedData.content !== undefined) newS.header.nameAR = stripHtml(updatedData.content);
      if (id === 'tagline' && updatedData.content !== undefined) newS.header.tagline = stripHtml(updatedData.content);
      if (id === 'meta' && updatedData.content !== undefined) newS.footer.note = stripHtml(updatedData.content);

      return newS;
    });
  }, [selectedElement, setM]);

  const ed = useMemo(() => ({
    on: lens === 'edit',
    setH: (p) => setM(s => ({ ...s, header: { ...s.header, ...p } })),
    setF: (p) => setM(s => ({ ...s, footer: { ...s.footer, ...p } })),
    setReg: (i, p) => setM(s => ({ ...s, footer: { ...s.footer, regItems: s.footer.regItems.map((r, j) => j === i ? { ...r, ...p } : r) } })),
    onLogo,
    selectedElement,
    activeDragId,
    onSelectElement: setSelectedElement,
    onChangeElement,
    onUpdateElement: (id: string, content: string, extra?: any) => handleElementFormatUpdate({ content, attributes: extra?.attributes || {} }, id),
    onDeleteElement,
    canvasBounds: { width: 0, height: 0 },
  }), [lens, onLogo, setM, selectedElement, activeDragId, onChangeElement, onDeleteElement, handleElementFormatUpdate]);

  const m = active;

  return (
    <div className="pb">
      <style>{`
        .st-free-content {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          width: 100%;
        }
        .st-free-content > * {
          margin: 0 !important;
          box-sizing: border-box !important;
          word-break: break-word;
          pointer-events: none !important;
        }
      `}</style>
      <div className="pb-bar">
        <button className="pb-bar-back" onClick={() => onBack(lib, lib.activeId)}><Ic k="arrowL" />Templates</button>
        <span className="pb-bar-div" />
        <span className="pb-bar-doc-ic acc-green"><Ic k="layers" /></span>
        <div className="st-presetbar">
          <div className="st-preset-pick">
            <select className="st-preset-select" value={lib.activeId} onChange={e => switchPreset(e.target.value)} title="Switch stationery preset">
              {lib.presets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Ic k="chevD" className="st-preset-caret" />
          </div>
          <input className="st-preset-name" value={active.name} onChange={e => setM(s => ({ ...s, name: e.target.value }))} title="Rename this stationery" placeholder="Stationery name" />
          <div className="st-preset-acts">
            <button onClick={newPreset} title="New stationery"><Ic k="plus" /></button>
            <button onClick={dupPreset} title="Duplicate this stationery"><Ic k="copy" /></button>
            <button onClick={delPreset} disabled={lib.presets.length <= 1} title="Delete this stationery"><Ic k="trash" /></button>
          </div>
        </div>
        <span className="pb-bar-stat-count">{lib.presets.length} saved</span>
        <span className={`pb-bar-stat ${dirty ? 'is-dirty' : 'is-saved'}`}><Ic k={dirty ? 'dot' : 'check'} />{dirty ? 'Unsaved' : 'Saved'}</span>
        {dirty && <button className="pbtn pbtn--primary pbtn--sm" onClick={save}><Ic k="save" />Save</button>}
        <div className="pb-bar-sp" />

        <span className="pb-bar-lbl">Mode</span>
        <div className="st-mode">
          <button className={m.mode === 'letterhead' ? 'is-active' : ''} onClick={() => setM(s => ({ ...s, mode: 'letterhead' }))}><Ic k="layers" />Letterhead</button>
          <button className={m.mode === 'preprinted' ? 'is-active' : ''} onClick={() => setM(s => ({ ...s, mode: 'preprinted' }))}><Ic k="file" />Pre-printed</button>
        </div>
        <span className="pb-srv"><Ic k="check" />Pickable per document in the builder</span>

      </div>

      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} sensors={sensors}>
        <div className="pb-work" onPointerDown={() => setSelectedElement(null)}>
          <div className="pb-side" onPointerDown={(e) => e.stopPropagation()}>
            <div className="pb-side-head">
              <span className="pb-side-h-t">Stationery</span>
              <span className="pb-side-h-s">Header · Footer</span>
            </div>
            <div className="pb-side-scroll">
              <Rail m={m} set={setM} />
              {(selectedElement === 'regBand' || selectedElement === 'rule') && (
                <Section
                  title="Format Element"
                  icon="layers"
                  open={true}
                  onToggle={() => { }}
                  hint="Drag to move, handles to resize."
                >
                  <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                      {selectedElement === 'regBand' ? 'Registration band' : 'Brand rule'}: drag on canvas to move, use handles to resize.
                    </p>
                    <button
                      style={{ padding: '6px 12px', background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                      onClick={() => {
                        if (selectedElement === 'regBand') setM((s: any) => ({ ...s, footer: { ...s.footer, regBand: false } }));
                        if (selectedElement === 'rule') setM((s: any) => ({ ...s, header: { ...s.header, rule: false } }));
                        setSelectedElement(null);
                      }}
                    >
                      🗑 Remove {selectedElement === 'regBand' ? 'Registration Band' : 'Brand Rule'}
                    </button>
                  </div>
                </Section>
              )}
            {selectedElementData && (
                <Section
                  title="Format Element"
                  icon="layers"
                  open={true}
                  onToggle={() => { }}
                  hint="Formatting options for the selected element."
                >
                  <SectionFormatPanel
                    section={selectedElementData}
                    onClose={() => setSelectedElement(null)}
                    onUpdate={handleElementFormatUpdate}
                    placeholders={placeholders || []}
                    relation_table={relation_table}
                    isEmbedded={true}
                    hidePresetSelector={true}
                  />
                </Section>
              )}
            </div>
          </div>
          <Stage m={m} type={type} setType={setType} showCont={showCont} setShowCont={setShowCont} sample={sample} setSample={setSample} ed={ed} lens={lens} setLens={setLens} count={lib.presets.length} />
        </div>
        <DragOverlay>
          {activeDragId ? (
            <div style={{ padding: '8px 12px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>
              Dragging element
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {toast && <div className="pb-toast"><Ic k="check" />{toast}</div>}

    </div>
  );
}
